import React from 'react';
import { Link } from 'react-router-dom';

/**
 * feat-319 — the `Role | Prompt` cells of the shift transcript header card.
 *
 * Renders a Fragment of two grid cells so they slot into the EXISTING
 * `grid grid-cols-1 md:grid-cols-3 gap-4` after Duration (design/NOTES.md §1):
 * at md they wrap onto row 2, below Server/Started/Duration.
 *
 * Pure: everything comes from props (GET /shifts/{id} fields, mapped by
 * ShiftTranscriptView). No metric math, no fetching.
 */

export const ROLE_PROMPT_COPY = {
  roleLabel: 'Role',
  promptLabel: 'Prompt',
  serverDefaultNote: '(server default)',
  emptyRole: '—', // —
  builtInDefault: 'Built-in default',
  unknownVersion: 'Unknown version',
  notCurrent: 'Not current',
  notCurrentTitle: 'This shift was scored with a version that is no longer active',
  separator: ' · v', // " · v"
} as const;

export interface ShiftRolePromptRowProps {
  /** shift.role (raw). */
  shiftRole?: string | null;
  /** COALESCE(shift.role, server.default_role). */
  effectiveRole?: string | null;
  /** tenant_role.display_name for the effective role; null when no match. */
  roleDisplayName?: string | null;
  /** shift.prompt_template_version_id. */
  promptTemplateVersionId?: string | null;
  promptTemplateName?: string | null;
  promptTemplateRole?: string | null;
  promptTemplateTenantId?: string | null;
  promptVersionNumber?: number | string | null;
  promptVersionIsActive?: boolean | null;
}

const isBlank = (v: string | null | undefined): boolean => v == null || String(v).trim() === '';

/** `/prompt-management?tenantId=…&role=…&versionId=…`, each value URL-encoded. */
export function buildPromptManagementHref(
  tenantId: string | null | undefined,
  role: string | null | undefined,
  versionId: string,
): string {
  const params = new URLSearchParams();
  if (!isBlank(tenantId)) params.set('tenantId', String(tenantId));
  if (!isBlank(role)) params.set('role', String(role));
  params.set('versionId', versionId);
  return `/prompt-management?${params.toString()}`;
}

const ShiftRolePromptRow: React.FC<ShiftRolePromptRowProps> = ({
  shiftRole,
  effectiveRole,
  roleDisplayName,
  promptTemplateVersionId,
  promptTemplateName,
  promptTemplateRole,
  promptTemplateTenantId,
  promptVersionNumber,
  promptVersionIsActive,
}) => {
  // ── Role ──
  const roleText = !isBlank(roleDisplayName)
    ? (roleDisplayName as string)
    : !isBlank(effectiveRole)
      ? (effectiveRole as string)
      : null;
  // effective_role came from server.default_role iff shift.role is NULL/empty.
  const fromServerDefault = roleText != null && isBlank(shiftRole);

  // ── Prompt ──
  let promptValue: React.ReactNode;
  if (isBlank(promptTemplateVersionId)) {
    promptValue = ROLE_PROMPT_COPY.builtInDefault;
  } else if (isBlank(promptTemplateName)) {
    promptValue = ROLE_PROMPT_COPY.unknownVersion;
  } else {
    const versionSuffix =
      promptVersionNumber != null && String(promptVersionNumber) !== ''
        ? `${ROLE_PROMPT_COPY.separator}${promptVersionNumber}`
        : '';
    promptValue = (
      <>
        <Link
          to={buildPromptManagementHref(
            promptTemplateTenantId,
            promptTemplateRole,
            promptTemplateVersionId as string,
          )}
          className="text-lg font-medium text-blue-600 hover:text-blue-800"
          data-testid="shift-prompt-link"
        >
          {promptTemplateName}
          {versionSuffix}
        </Link>
        {!promptVersionIsActive && (
          <span
            className="ml-2 px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-xs font-medium align-middle whitespace-nowrap"
            title={ROLE_PROMPT_COPY.notCurrentTitle}
            data-testid="shift-prompt-not-current"
          >
            {ROLE_PROMPT_COPY.notCurrent}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <div className="min-w-0" data-testid="shift-role-cell">
        <h3 className="text-sm font-semibold text-gray-500">{ROLE_PROMPT_COPY.roleLabel}</h3>
        <p className="text-lg break-words">
          {roleText ?? ROLE_PROMPT_COPY.emptyRole}
          {fromServerDefault && (
            <span
              className="ml-1.5 text-sm font-normal text-gray-500 whitespace-nowrap"
              data-testid="shift-role-server-default"
            >
              {ROLE_PROMPT_COPY.serverDefaultNote}
            </span>
          )}
        </p>
      </div>
      <div className="min-w-0" data-testid="shift-prompt-cell">
        <h3 className="text-sm font-semibold text-gray-500">{ROLE_PROMPT_COPY.promptLabel}</h3>
        <p className="text-lg break-words">{promptValue}</p>
      </div>
    </>
  );
};

export default ShiftRolePromptRow;
